import { config } from "@/data/config";
import { apiClient } from "@/utils/api";

/** Canonical URL for a route pathname (no trailing slash except `/`). */
export function canonicalUrl(pathname: string): string {
	const path = pathname === "/" ? "/" : pathname.replace(/\/+$/, "") || "/";
	return `${config.siteUrl}${path}`;
}

const buildOgImageUrl = (title: string, description?: string): string => {
	const params = new URLSearchParams({ title });
	if (description) params.set("description", description);
	return `${apiClient.baseUrl}/api/seo/og-image?${params.toString()}`;
};

export const seo = ({
	title,
	description,
	keywords,
	image = buildOgImageUrl(title, description),
	type = "WebSite",
	author = "Francis Baker",
	datePublished,
	dateModified,
	hasPart,
	faqItems,
}: {
	title: string;
	description?: string;
	image?: string;
	keywords?: string;
	type?:
		| "WebSite"
		| "Article"
		| "CollectionPage"
		| "SoftwareApplication"
		| "FAQPage";
	author?: string;
	datePublished?: string;
	dateModified?: string;
	hasPart?: Array<{
		"@type": string;
		name: string;
		description?: string;
		url: string;
	}>;
	faqItems?: Array<{ question: string; answer: string }>;
}) => {
	const metaTags = [
		{ title },
		{ name: "description", content: description },
		{ name: "keywords", content: keywords },
		{ name: "twitter:title", content: title },
		{ name: "twitter:description", content: description },
		{ name: "twitter:creator", content: "@frncsbkr" },
		{ name: "twitter:site", content: "@frncsbkr" },
		{ name: "og:type", content: "website" },
		{ name: "og:title", content: title },
		{ name: "og:description", content: description },
		...(image
			? [
					{ name: "twitter:image", content: image },
					{ name: "twitter:card", content: "summary_large_image" },
					{ name: "og:image", content: image },
				]
			: []),
	];

	const baseSchema = {
		"@context": "https://schema.org",
		"@type": type,
		name: title,
		description,
		image,
	};

	let jsonLDSchema: object;
	if (type === "Article") {
		jsonLDSchema = {
			...baseSchema,
			headline: title,
			author: {
				"@type": "Person",
				name: author,
			},
			datePublished,
			dateModified,
		};
	} else if (type === "CollectionPage") {
		jsonLDSchema = {
			...baseSchema,
			...(hasPart ? { hasPart } : {}),
		};
	} else if (type === "SoftwareApplication") {
		jsonLDSchema = {
			...baseSchema,
			applicationCategory: "CommunicationApplication",
			offers: {
				"@type": "Offer",
				price: "0",
			},
			url: config.siteUrl,
		};
	} else if (type === "FAQPage") {
		const items = faqItems ?? [];
		jsonLDSchema = {
			...baseSchema,
			mainEntity: items.map((item) => ({
				"@type": "Question",
				name: item.question,
				acceptedAnswer: {
					"@type": "Answer",
					text: item.answer,
				},
			})),
		};
	} else {
		jsonLDSchema = baseSchema;
	}

	return {
		meta: metaTags,
		scripts: [
			{
				type: "application/ld+json",
				children: JSON.stringify(jsonLDSchema),
			},
		],
	};
};
